import { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getApiUrl } from '@/config/api';

interface SessionStats {
    athlete_name?: string;
    jersey?: string;
    position?: string;
    total_distance?: number | null;
    total_player_load?: number | null;
    max_vel?: number | null;
    mean_vel?: number | null;
    mean_heart_rate?: number | null;
    max_heart_rate?: number | null;
    hsr_efforts?: number | null;
    player_load_per_minute?: number | null;
    high_speed_distance?: number | null;
    max_accel?: number | null;
    max_decel?: number | null;
    hr_band1_pct?: number | null;
    hr_band2_pct?: number | null;
    hr_band3_pct?: number | null;
    hr_band4_pct?: number | null;
    hr_band5_pct?: number | null;
    hr_band6_pct?: number | null;
    hr_band7_pct?: number | null;
    hr_band8_pct?: number | null;
    [key: string]: any;
}

interface CareerStats {
    avg_distance?: number;
    avg_player_load?: number;
    avg_max_velocity?: number;
    total_sessions?: number;
    [key: string]: any;
}

interface CatapultAIInsightsProps {
    athleteId: string;
    sessionStats: SessionStats | null;
    careerStats: CareerStats | null;
    activityName: string;
}

export default function CatapultAIInsights({
    athleteId,
    sessionStats,
    careerStats,
    activityName,
}: CatapultAIInsightsProps) {
    const [insights, setInsights] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const generateInsights = useCallback(async () => {
        if (!sessionStats) return;
        setLoading(true);
        setError(null);
        setInsights(null);

        try {
            const token = localStorage.getItem('access');
            const resp = await fetch(getApiUrl(`/api/catapult/athletes/${athleteId}/insights/`), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    session_stats: sessionStats,
                    career_stats: careerStats || {},
                    activity_name: activityName,
                }),
            });

            if (!resp.ok) {
                const data = await resp.json().catch(() => ({}));
                throw new Error(data.detail || `Request failed (${resp.status})`);
            }

            const data = await resp.json();
            setInsights(data.insights);
        } catch (err: any) {
            setError(err.message || 'Failed to generate insights');
        } finally {
            setLoading(false);
        }
    }, [athleteId, sessionStats, careerStats, activityName]);

    if (!sessionStats) return null;

    return (
        <div className="mt-3">
            {!insights && !loading && !error && (
                <button
                    onClick={generateInsights}
                    className="cgm-button w-full px-3 py-2 text-xs font-medium cursor-pointer"
                >
                    Generate AI Insights
                </button>
            )}

            {loading && (
                <div className="cgm-card p-4">
                    <div className="flex items-center gap-2 cgm-text-secondary text-xs">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 cgm-spinner" />
                        Analyzing session data...
                    </div>
                </div>
            )}

            {error && (
                <div className="cgm-alert-error p-3 text-xs">
                    <p>{error}</p>
                    <button
                        onClick={generateInsights}
                        className="cgm-button mt-2 px-2 py-1 text-xs cursor-pointer"
                    >
                        Retry
                    </button>
                </div>
            )}

            {insights && (
                <div className="cgm-card p-4">
                    <div className="prose prose-sm max-w-none cgm-text-primary text-xs leading-relaxed
                        [&_h2]:text-xs [&_h2]:font-bold [&_h2]:cgm-text-primary [&_h2]:mt-3 [&_h2]:mb-1.5
                        [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:cgm-text-primary [&_h3]:mt-2 [&_h3]:mb-1
                        [&_ul]:my-1 [&_ul]:pl-4 [&_li]:my-0.5
                        [&_ol]:my-1 [&_ol]:pl-4
                        [&_strong]:cgm-text-primary
                        [&_p]:my-1
                    ">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{insights}</ReactMarkdown>
                    </div>
                    <button
                        onClick={generateInsights}
                        className="mt-3 text-xs cgm-text-muted cursor-pointer hover:underline"
                    >
                        Regenerate
                    </button>
                </div>
            )}
        </div>
    );
}
